import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft,
  ChevronRight,
  User,
  CheckCircle,
  XCircle,
  Award,
  Clock,
  Calendar,
  TrendingUp,
  ArrowLeft,
  FileText,
  Search,
  ChevronDown,
  ChevronUp,
  Eye,
  Table,
  Users,
  Download
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import usuarioExamenService from '../../services/usuarioExamenService';
import respuestaService from '../../services/respuestaService';
import examenService from '../../services/examenService';
import pdfService from '../../services/pdfService';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Loader from '../../components/ui/Loader';
import Input from '../../components/ui/Input';
import ImageViewer from '../../components/common/ImageViewer';

const RevisionExamen = ({ examenId, onVolver }) => {
  const [loading, setLoading] = useState(true);
  const [examen, setExamen] = useState(null);
  const [usuariosExamen, setUsuariosExamen] = useState([]);
  const [usuarioActualIndex, setUsuarioActualIndex] = useState(0);
  const [respuestasUsuario, setRespuestasUsuario] = useState([]);
  const [loadingRespuestas, setLoadingRespuestas] = useState(false);
  const [resultadoFinal, setResultadoFinal] = useState(null);
  const [busqueda, setBusqueda] = useState('');
  const [usuariosFiltrados, setUsuariosFiltrados] = useState([]);
  const [mostrarDetalle, setMostrarDetalle] = useState(false);
  const [vistaActual, setVistaActual] = useState('tabla'); // 'tabla' o 'carrusel'
  const [resultadosTabla, setResultadosTabla] = useState([]);
  const [loadingTabla, setLoadingTabla] = useState(false);

  useEffect(() => {
    cargarDatos();
  }, [examenId]);

  useEffect(() => {
    if (usuariosExamen.length > 0) {
      cargarRespuestasUsuario(usuariosExamen[usuarioActualIndex].usuarioId);
    }
  }, [usuarioActualIndex, usuariosExamen]);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      
      // Cargar información del examen
      const examenData = await examenService.getExamenById(examenId);
      setExamen(examenData);

      // Cargar usuarios que han tomado el examen
      const usuarios = await usuarioExamenService.obtenerUsuariosExamen(examenId);
      
      // Filtrar solo usuarios que han finalizado o completado el examen
      const usuariosFinalizados = usuarios.filter(u => 
        u.estado === 'FINALIZADO' || 
        u.estado === 'COMPLETADO' || 
        u.estado === 'TIEMPO_AGOTADO'
      );

      setUsuariosExamen(usuariosFinalizados);
      setUsuariosFiltrados(usuariosFinalizados);

      if (usuariosFinalizados.length === 0) {
        toast.info('Aún no hay usuarios que hayan completado este examen');
      } else {
        // Cargar resultados para la tabla
        cargarResultadosTabla(usuariosFinalizados, examenData);
      }
    } catch (error) {
      console.error('Error al cargar datos:', error);
      toast.error('Error al cargar la información del examen');
    } finally {
      setLoading(false);
    }
  };

  // Cargar resultados de todos los usuarios para la tabla
  const cargarResultadosTabla = async (usuarios, examenData) => {
    try {
      setLoadingTabla(true);
      const resultadosPromises = usuarios.map(async (usuario) => {
        try {
          const resultado = await respuestaService.getResultadoFinalExamen(
            usuario.usuarioId,
            examenId,
            examenData.preguntas.length
          );
          return {
            ...usuario,
            ...resultado
          };
        } catch (error) {
          console.error(`Error al cargar resultado de usuario ${usuario.usuarioId}:`, error);
          return {
            ...usuario,
            respuestasCorrectas: 0,
            respuestasIncorrectas: 0,
            porcentajeAcierto: 0,
            nivelRendimiento: 'N/A'
          };
        }
      });

      const resultados = await Promise.all(resultadosPromises);
      setResultadosTabla(resultados);
    } catch (error) {
      console.error('Error al cargar resultados de tabla:', error);
    } finally {
      setLoadingTabla(false);
    }
  };

  // Filtrar usuarios por búsqueda
  useEffect(() => {
    if (busqueda.trim() === '') {
      setUsuariosFiltrados(usuariosExamen);
    } else {
      const filtrados = usuariosExamen.filter(u =>
        u.usuarioNombre.toLowerCase().includes(busqueda.toLowerCase())
      );
      setUsuariosFiltrados(filtrados);
      // Si hay resultados, ir al primero
      if (filtrados.length > 0) {
        const indexEnOriginal = usuariosExamen.findIndex(u => u.usuarioId === filtrados[0].usuarioId);
        setUsuarioActualIndex(indexEnOriginal);
      }
    }
  }, [busqueda, usuariosExamen]);

  const cargarRespuestasUsuario = async (usuarioId) => {
    try {
      setLoadingRespuestas(true);
      
      // Obtener todas las respuestas del usuario
      const todasRespuestas = await respuestaService.getRespuestasByUsuario(usuarioId);
      
      // Filtrar solo las respuestas de este examen
      const respuestasExamen = todasRespuestas.filter(r => r.examenId === examenId);

      // Crear un mapa de respuestas por preguntaId para acceso rápido
      const respuestasMap = {};
      respuestasExamen.forEach(r => {
        respuestasMap[r.preguntaId] = r;
      });

      // Construir el detalle completo de cada pregunta con su respuesta
      const detalleRespuestas = examen.preguntas.map(pregunta => {
        const respuesta = respuestasMap[pregunta.id];
        const opcionSeleccionada = pregunta.opciones?.find(o => o.id === respuesta?.opcionId);
        const opcionCorrecta = pregunta.opciones?.find(o => o.esCorrecta);

        return {
          pregunta: pregunta.texto,
          preguntaImagen: pregunta.imagen,
          preguntaPeso: pregunta.peso || 1,
          opcionSeleccionada: opcionSeleccionada?.texto || 'Sin responder',
          opcionSeleccionadaImagen: opcionSeleccionada?.imagen,
          opcionCorrecta: opcionCorrecta?.texto || 'N/A',
          opcionCorrectaImagen: opcionCorrecta?.imagen,
          esCorrecta: respuesta?.correcta || false,
          respondio: !!respuesta
        };
      });

      setRespuestasUsuario(detalleRespuestas);

      // Obtener resultado final con nivel de rendimiento
      try {
        const resultado = await respuestaService.getResultadoFinalExamen(
          usuarioId, 
          examenId, 
          examen.preguntas.length
        );
        setResultadoFinal(resultado);
      } catch (errorResultado) {
        console.error('Error al cargar resultado final:', errorResultado);
        setResultadoFinal(null);
      }
    } catch (error) {
      console.error('Error al cargar respuestas:', error);
      toast.error('Error al cargar las respuestas del usuario');
    } finally {
      setLoadingRespuestas(false);
    }
  };

  const handleAnterior = () => {
    if (usuarioActualIndex > 0) {
      setUsuarioActualIndex(usuarioActualIndex - 1);
    }
  };

  const handleSiguiente = () => {
    if (usuarioActualIndex < usuariosExamen.length - 1) {
      setUsuarioActualIndex(usuarioActualIndex + 1);
    }
  };

  const getEstadoBadge = (estado) => {
    const config = {
      'FINALIZADO': { color: 'bg-green-100 text-green-800 border-green-200', text: 'Finalizado' },
      'COMPLETADO': { color: 'bg-blue-100 text-blue-800 border-blue-200', text: 'Completado' },
      'TIEMPO_AGOTADO': { color: 'bg-orange-100 text-orange-800 border-orange-200', text: 'Tiempo Agotado' },
    };
    const { color, text } = config[estado] || config['FINALIZADO'];
    return <span className={`px-2 py-1 rounded-full text-xs font-medium border ${color}`}>{text}</span>;
  };

  const getNivelRendimientoBadge = (nivel) => {
    const config = {
      'SUPERIOR': { color: 'bg-purple-100 text-purple-800 border-purple-200', text: 'Superior' },
      'NORMAL_SUPERIOR': { color: 'bg-blue-100 text-blue-800 border-blue-200', text: 'Normal Superior' },
      'NORMAL_PROMEDIO': { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', text: 'Normal Promedio' },
      'INFERIOR': { color: 'bg-red-100 text-red-800 border-red-200', text: 'Inferior' },
    };
    const { color, text } = config[nivel] || { color: 'bg-gray-100 text-gray-800 border-gray-200', text: nivel };
    return <span className={`px-3 py-1.5 rounded-full text-sm font-medium border ${color}`}>{text}</span>;
  };

  const handleDescargarPDF = async (usuario, resultado) => {
    try {
      toast.loading('Generando PDF...', { id: 'pdf-loading' });
      
      await pdfService.descargarReportePDF(
        usuario,
        resultado
      );
      
      toast.success('PDF descargado correctamente', { id: 'pdf-loading' });
    } catch (error) {
      console.error('Error al descargar PDF:', error);
      toast.error('Error al generar el PDF', { id: 'pdf-loading' });
    }
  };

  const formatearFecha = (fecha) => {
    if (!fecha) return 'N/A';
    return new Date(fecha).toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const calcularTiempoUsado = (fechaInicio, fechaFin) => {
    if (!fechaInicio || !fechaFin) return 'N/A';
    const inicio = new Date(fechaInicio);
    const fin = new Date(fechaFin);
    const diffMs = fin - inicio;
    const diffMins = Math.floor(diffMs / 60000);
    const diffSecs = Math.floor((diffMs % 60000) / 1000);
    return `${diffMins}m ${diffSecs}s`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader size="lg" />
      </div>
    );
  }

  if (!examen) {
    return (
      <div className="text-center py-12">
        <p className="text-neutral-600">No se pudo cargar la información del examen</p>
        <Button variant="outline" onClick={onVolver} className="mt-4">
          Volver
        </Button>
      </div>
    );
  }

  if (usuariosExamen.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">Revisión de Examen</h1>
            <p className="text-neutral-600">{examen.nombre}</p>
          </div>
          <Button variant="outline" onClick={onVolver}>
            <ArrowLeft className="w-4 h-4" />
            Volver
          </Button>
        </div>

        <Card>
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-neutral-400" />
            </div>
            <h3 className="text-lg font-semibold text-neutral-900 mb-2">
              No hay respuestas para revisar
            </h3>
            <p className="text-neutral-600">
              Aún no hay usuarios que hayan completado este examen
            </p>
          </div>
        </Card>
      </div>
    );
  }

  const usuarioActual = usuariosExamen[usuarioActualIndex];
  const respuestasCorrectas = respuestasUsuario.filter(r => r.esCorrecta).length;
  const respuestasIncorrectas = respuestasUsuario.filter(r => r.respondio && !r.esCorrecta).length;
  const sinResponder = respuestasUsuario.filter(r => !r.respondio).length;
  const porcentaje = examen.preguntas.length > 0 
    ? ((respuestasCorrectas / examen.preguntas.length) * 100).toFixed(1) 
    : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Revisión de Examen</h1>
          <p className="text-neutral-600">{examen.nombre}</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Selector de Vista */}
          <div className="flex bg-neutral-100 rounded-lg p-1">
            <button
              onClick={() => setVistaActual('tabla')}
              className={`flex items-center gap-2 px-4 py-2 rounded-md transition-all ${
                vistaActual === 'tabla'
                  ? 'bg-white text-primary shadow-sm font-medium'
                  : 'text-neutral-600 hover:text-neutral-900'
              }`}
            >
              <Table className="w-4 h-4" />
              <span className="hidden sm:inline">Tabla General</span>
            </button>
            <button
              onClick={() => setVistaActual('carrusel')}
              className={`flex items-center gap-2 px-4 py-2 rounded-md transition-all ${
                vistaActual === 'carrusel'
                  ? 'bg-white text-primary shadow-sm font-medium'
                  : 'text-neutral-600 hover:text-neutral-900'
              }`}
            >
              <Users className="w-4 h-4" />
              <span className="hidden sm:inline">Vista Detallada</span>
            </button>
          </div>
          
          <Button variant="outline" onClick={onVolver}>
            <ArrowLeft className="w-4 h-4" />
            Volver
          </Button>
        </div>
      </div>

      {/* VISTA DE TABLA */}
      {vistaActual === 'tabla' && (
        <>
          <Card>
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-neutral-900 flex items-center gap-2">
                <Table className="w-5 h-5 text-primary" />
                Resultados Generales de Todos los Postulantes
              </h3>
              <p className="text-sm text-neutral-600">Vista general de rendimiento de todos los participantes</p>
            </div>

            {loadingTabla ? (
              <div className="flex items-center justify-center py-12">
                <Loader size="md" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-neutral-50 border-b-2 border-neutral-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider">
                        #
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider">
                        Postulante
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-neutral-700 uppercase tracking-wider">
                        Correctas
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-neutral-700 uppercase tracking-wider">
                        Incorrectas
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-neutral-700 uppercase tracking-wider">
                        Porcentaje
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-neutral-700 uppercase tracking-wider">
                        Puntos
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-neutral-700 uppercase tracking-wider">
                        Nivel
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-neutral-700 uppercase tracking-wider">
                        Estado
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-neutral-700 uppercase tracking-wider">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-neutral-200">
                    {resultadosTabla.map((resultado, index) => (
                      <tr key={resultado.usuarioId} className="hover:bg-neutral-50 transition-colors">
                        <td className="px-4 py-3 text-sm text-neutral-700 font-medium">
                          {index + 1}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                              <User className="w-5 h-5 text-primary" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-neutral-900">
                                {resultado.usuarioNombre}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="inline-flex items-center justify-center w-8 h-8 bg-green-100 text-green-700 rounded-full text-sm font-bold">
                            {resultado.respuestasCorrectas || 0}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="inline-flex items-center justify-center w-8 h-8 bg-red-100 text-red-700 rounded-full text-sm font-bold">
                            {resultado.respuestasIncorrectas || 0}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="text-sm font-semibold text-blue-700">
                            {resultado.porcentajeAcierto?.toFixed(1) || '0.0'}%
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="text-sm font-semibold text-neutral-900">
                            {resultado.puntosObtenidos || 0}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {getNivelRendimientoBadge(resultado.nivelRendimiento || 'N/A')}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {getEstadoBadge(resultado.estado)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const userIndex = usuariosExamen.findIndex(u => u.usuarioId === resultado.usuarioId);
                                setUsuarioActualIndex(userIndex);
                                setVistaActual('carrusel');
                              }}
                            >
                              <Eye className="w-4 h-4" />
                              Ver Detalle
                            </Button>
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => handleDescargarPDF(resultado, resultado)}
                            >
                              <Download className="w-4 h-4" />
                              PDF
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}

      {/* VISTA DE CARRUSEL */}
      {vistaActual === 'carrusel' && (
        <>
      <Card>
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <Input
                type="text"
                placeholder="Buscar postulante por nombre..."
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                icon={Search}
                fullWidth
              />
            </div>
            {busqueda && (
              <Button
                variant="outline"
                onClick={() => setBusqueda('')}
                size="sm"
              >
                Limpiar
              </Button>
            )}
          </div>
          
          {busqueda && (
            <div className="flex items-center justify-between">
              {usuariosFiltrados.length > 0 ? (
                <div className="text-sm text-neutral-600">
                  📋 Mostrando <span className="font-semibold text-primary">{usuariosFiltrados.length}</span> de <span className="font-semibold">{usuariosExamen.length}</span> postulante(s)
                </div>
              ) : (
                <div className="text-sm text-red-600">
                  ❌ No se encontraron postulantes con ese nombre
                </div>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Carrusel de Usuarios */}
      <Card>
        <div className="flex items-center justify-between gap-4">
          {/* Botón Anterior */}
          <Button
            variant="outline"
            onClick={handleAnterior}
            disabled={usuarioActualIndex === 0}
            className="flex-shrink-0"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>

          {/* Información del Usuario Actual */}
          <div className="flex-1">
            <div className="flex items-center justify-center gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                  <User className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-neutral-900">
                    {usuarioActual.usuarioNombre}
                  </h2>
                  <div className="flex items-center gap-2">
                    <p className="text-sm text-neutral-600">
                      Usuario {usuarioActualIndex + 1} de {usuariosExamen.length}
                    </p>
                    
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                {resultadoFinal && resultadoFinal.nivelRendimiento && (
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-neutral-500">•</span>
                        {getNivelRendimientoBadge(resultadoFinal.nivelRendimiento)}
                      </div>
                    )}
                <Badge variant="secondary">
                  {usuarioActual.puntajeObtenido} / {usuarioActual.puntajeMaximo} pts
                </Badge>
              </div>
            </div>
          </div>

          {/* Botón Siguiente */}
          <Button
            variant="outline"
            onClick={handleSiguiente}
            disabled={usuarioActualIndex === usuariosExamen.length - 1}
            className="flex-shrink-0"
          >
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>

        {/* Indicadores de posición */}
        <div className="flex justify-center gap-2 mt-4">
          {usuariosExamen.map((_, index) => (
            <button
              key={index}
              onClick={() => setUsuarioActualIndex(index)}
              className={`w-2 h-2 rounded-full transition-all ${
                index === usuarioActualIndex
                  ? 'bg-primary w-8'
                  : 'bg-neutral-300 hover:bg-neutral-400'
              }`}
            />
          ))}
        </div>
      </Card>

      {/* Leyenda de Niveles de Rendimiento */}
      <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-blue-600" />
            <h3 className="font-semibold text-neutral-900">
              Escala de Rendimiento - Total de matrices: {examen.preguntas.length}
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="bg-white rounded-lg p-3 border border-purple-200">
              <p className="text-xs text-neutral-600 mb-1">Cero errores</p>
              <p className="font-bold text-purple-700">Superior</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-blue-200">
              <p className="text-xs text-neutral-600 mb-1">Dos errores</p>
              <p className="font-bold text-blue-700">Normal Superior</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-yellow-200">
              <p className="text-xs text-neutral-600 mb-1">De tres a cuatro errores</p>
              <p className="font-bold text-yellow-700">Normal Promedio</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-red-200">
              <p className="text-xs text-neutral-600 mb-1">Cinco errores a más</p>
              <p className="font-bold text-red-700">Inferior</p>
            </div>
          </div>
        </div>
      </Card>

      {/* Resultado Final con Nivel de Rendimiento */}
      {resultadoFinal && resultadoFinal.porcentajeAciertos !== undefined && (
        <Card className="bg-gradient-to-br from-primary-50 to-secondary-50 border-primary-200">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-primary rounded-full flex items-center justify-center">
                <Award className="w-8 h-8 text-white" />
              </div>
              <div>
                <p className="text-sm text-neutral-600 mb-1">Nivel de Rendimiento</p>
                <div className="flex items-center gap-3">
                  {getNivelRendimientoBadge(resultadoFinal.nivelRendimiento)}
                  <span className="text-2xl font-bold text-primary">
                    {resultadoFinal.porcentajeAciertos?.toFixed(1) || '0.0'}%
                  </span>
                </div>
              </div>
            </div>
            <div className="flex gap-6">
              <div className="text-center">
                <p className="text-sm text-neutral-600">Respuestas Correctas</p>
                <p className="text-2xl font-bold text-green-600">{resultadoFinal.respuestasCorrectas || 0}</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-neutral-600">Respuestas Incorrectas</p>
                <p className="text-2xl font-bold text-red-600">{resultadoFinal.respuestasIncorrectas || 0}</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-neutral-600">Puntos Totales</p>
                <p className="text-2xl font-bold text-blue-600">{resultadoFinal.puntosTotales || 0}</p>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Resumen de Resultados */}
      <Card>
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-neutral-900 flex items-center gap-2">
            <Award className="w-5 h-5 text-primary" />
            Resumen del Examen: {examen.nombre}
          </h3>
          <p className="text-sm text-neutral-600">Resultados del postulante en este examen</p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-neutral-600">Correctas</p>
                <p className="text-2xl font-bold text-green-700">{respuestasCorrectas}</p>
              </div>
            </div>
          </div>

          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                <XCircle className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-neutral-600">Incorrectas</p>
                <p className="text-2xl font-bold text-red-700">{respuestasIncorrectas}</p>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                <Award className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-neutral-600">Calificación</p>
                <p className="text-2xl font-bold text-blue-700">{porcentaje}%</p>
              </div>
            </div>
          </div>

          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center flex-shrink-0">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-neutral-600">Tiempo Usado</p>
                <p className="text-lg font-bold text-purple-700">
                  {calcularTiempoUsado(usuarioActual.fechaInicio, usuarioActual.fechaFinalizacion)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Información Adicional */}
      <Card>
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-neutral-900 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary" />
            Información del Proceso
          </h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center gap-3">
            <Calendar className="w-5 h-5 text-neutral-400" />
            <div>
              <p className="text-sm text-neutral-600">Fecha de Inicio</p>
              <p className="font-medium text-neutral-900">
                {formatearFecha(usuarioActual.fechaInicio)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Calendar className="w-5 h-5 text-neutral-400" />
            <div>
              <p className="text-sm text-neutral-600">Fecha de Finalización</p>
              <p className="font-medium text-neutral-900">
                {formatearFecha(usuarioActual.fechaFinalizacion)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <TrendingUp className="w-5 h-5 text-neutral-400" />
            <div>
              <p className="text-sm text-neutral-600">Intentos</p>
              <p className="font-medium text-neutral-900">{usuarioActual.intentos}</p>
            </div>
          </div>
        </div>
      </Card>

      {/* Detalle de Respuestas - Colapsable */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-neutral-900 flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Detalle de Respuestas - {examen.nombre}
            </h3>
            <p className="text-sm text-neutral-600">
              {mostrarDetalle 
                ? 'Ocultando el detalle de cada pregunta y respuesta' 
                : 'Ver el detalle completo de cada pregunta y respuesta del postulante'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="success"
              onClick={() => handleDescargarPDF(usuarioActual, resultadoFinal)}
              className="flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Descargar PDF
            </Button>
            <Button
              variant={mostrarDetalle ? "primary" : "outline"}
              onClick={() => setMostrarDetalle(!mostrarDetalle)}
              className="flex items-center gap-2"
            >
              <Eye className="w-4 h-4" />
              {mostrarDetalle ? (
                <>
                  Ocultar Detalle
                  <ChevronUp className="w-4 h-4" />
                </>
              ) : (
                <>
                  Ver Detalle
                  <ChevronDown className="w-4 h-4" />
                </>
              )}
            </Button>
          </div>
        </div>

        {mostrarDetalle && (
          <>
            {loadingRespuestas ? (
              <div className="flex items-center justify-center py-12">
                <Loader size="md" />
              </div>
            ) : (
              <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2">
                {respuestasUsuario.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card
                  className={`${
                    !item.respondio
                      ? 'border-gray-300 bg-gray-50'
                      : item.esCorrecta
                      ? 'border-green-200 bg-green-50'
                      : 'border-red-200 bg-red-50'
                  }`}
                >
                  <div className="space-y-3">
                    {/* Pregunta */}
                    <div className="flex items-start gap-3">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                          !item.respondio
                            ? 'bg-gray-200'
                            : item.esCorrecta
                            ? 'bg-green-100'
                            : 'bg-red-100'
                        }`}
                      >
                        <span
                          className={`text-sm font-bold ${
                            !item.respondio
                              ? 'text-gray-600'
                              : item.esCorrecta
                              ? 'text-green-700'
                              : 'text-red-700'
                          }`}
                        >
                          {index + 1}
                        </span>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <p className="font-semibold text-neutral-900 text-base">
                            {item.pregunta}
                          </p>
                          <Badge variant="secondary">
                            {item.preguntaPeso} {item.preguntaPeso === 1 ? 'punto' : 'puntos'}
                          </Badge>
                        </div>
                        {item.preguntaImagen && (
                          <div className="mt-3 flex justify-center bg-neutral-50 rounded-xl p-3 border border-neutral-200">
                            <ImageViewer
                              src={item.preguntaImagen}
                              alt="Pregunta"
                              className="w-full h-48 object-contain rounded-lg shadow-md bg-white"
                              containerClassName="w-full max-w-lg"
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Respuestas */}
                    <div className="pl-6 space-y-3">
                      {/* Respuesta del usuario */}
                      {item.respondio ? (
                        <div>
                          <p className="text-sm font-semibold text-neutral-700 mb-2 flex items-center gap-2">
                            {item.esCorrecta ? (
                              <CheckCircle className="w-4 h-4 text-green-600" />
                            ) : (
                              <XCircle className="w-4 h-4 text-red-600" />
                            )}
                            Respuesta del usuario:
                          </p>
                          <div
                            className={`p-3 rounded-lg border-2 ${
                              item.esCorrecta
                                ? 'bg-green-50 border-green-200'
                                : 'bg-red-50 border-red-200'
                            }`}
                          >
                            <p
                              className={`text-sm font-semibold ${
                                item.esCorrecta ? 'text-green-800' : 'text-red-800'
                              }`}
                            >
                              {item.opcionSeleccionada}
                            </p>
                            {item.opcionSeleccionadaImagen && (
                              <div className="mt-2 flex justify-center bg-white rounded-lg p-2 border border-neutral-200">
                                <ImageViewer
                                  src={item.opcionSeleccionadaImagen}
                                  alt="Opción seleccionada"
                                  className="w-full h-36 object-contain rounded-lg shadow-sm"
                                  containerClassName="w-full max-w-sm"
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="p-3 rounded-lg border-2 bg-gray-50 border-gray-200">
                          <p className="text-sm font-semibold text-gray-600">
                            ⚠️ Sin responder
                          </p>
                        </div>
                      )}

                      {/* Respuesta correcta (si falló o no respondió) */}
                      {(!item.esCorrecta || !item.respondio) && (
                        <div>
                          <p className="text-sm font-semibold text-neutral-700 mb-2 flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-600" />
                            Respuesta correcta:
                          </p>
                          <div className="p-3 rounded-lg border-2 bg-green-50 border-green-200">
                            <p className="text-sm font-semibold text-green-800">
                              {item.opcionCorrecta}
                            </p>
                            {item.opcionCorrectaImagen && (
                              <div className="mt-2 flex justify-center bg-white rounded-lg p-2 border border-neutral-200">
                                <ImageViewer
                                  src={item.opcionCorrectaImagen}
                                  alt="Opción correcta"
                                  className="w-full h-36 object-contain rounded-lg shadow-sm"
                                  containerClassName="w-full max-w-sm"
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
              </div>
            )}
          </>
        )}
        
        {!mostrarDetalle && !loadingRespuestas && (
          <div className="text-center py-8 bg-neutral-50 rounded-lg border-2 border-dashed border-neutral-300">
            <Eye className="w-12 h-12 text-neutral-400 mx-auto mb-3" />
            <p className="text-neutral-600 mb-2">
              El detalle de respuestas está oculto
            </p>
            <p className="text-sm text-neutral-500">
              Haz clic en "Ver Detalle" para mostrar todas las preguntas y respuestas
            </p>
          </div>
        )}
      </Card>
        </>
      )}
    </div>
  );
};

export default RevisionExamen;
